import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { Gift, Copy, Share2, Users, DollarSign, TrendingUp, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function ReferFriendPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [payouts, setPayouts] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [meResp, payoutsResp] = await Promise.all([
          api.get("/referral/me"),
          api.get("/referral/payouts"),
        ]);
        setData(meResp.data);
        setPayouts(payoutsResp.data);
      } catch {
        toast.error("Could not load referral info");
      }
    })();
  }, []);

  const referLink =
    data && typeof window !== "undefined"
      ? `${window.location.origin}/register?ref=${data.referral_code}`
      : "";

  const copy = (text, label = "Copied") => copyToClipboard(text, label);

  const shareNative = async () => {
    if (!referLink) return;
    const text = `Join me on Matrix to earn commissions from your shopping referrals. Sign up under me with my link:`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Refer a friend on Matrix", text, url: referLink });
      } catch {
        // user cancelled — ignore
      }
    } else {
      copy(`${text} ${referLink}`, "Message copied — paste it anywhere");
    }
  };

  if (!user) return null;

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow text-[#002FA7] flex items-center gap-2"><Gift size={14} /> Refer & earn</div>
          <h1 className="text-4xl tracking-tight font-black text-[#111827]">Refer a friend, earn 5% of every sale</h1>
          <p className="text-[#4B5563] mt-2 max-w-3xl">
            Share your personal sign-up link with friends or family. When they join Matrix and start making sales, the 10% commission pool is split 50/50 — <span className="font-bold text-[#002FA7]">5% to them, 5% to you</span> — for life. Every person you refer adds to your passive income.
          </p>
        </div>
      </div>

      {/* How it works */}
      <div className="card-flat p-6 mb-6">
        <div className="label-eyebrow text-[#002FA7] mb-3">How it works</div>
        <div className="grid md:grid-cols-3 gap-4">
          <Step n={1} title="Share your personal link" body="Send your link to friends and family who want to earn commissions selling Matrix Shopping." />
          <Step n={2} title="They sign up under you" body="Anyone who registers via your link becomes your downline — automatically credited to your account." />
          <Step n={3} title="You earn 5% on every sale they make" body={`When admin records a sale on one of their leads, the 10% commission splits 50/50 — 5% to them, 5% to you.`} />
        </div>
        <div className="mt-5 rounded-md bg-[#F0FDF4] border border-[#BBF7D0] p-4 text-sm text-[#166534]">
          <span className="font-bold">Example: </span>
          Your downline captures a customer who makes Matrix <span className="font-bold">$100,000</span> in profits. Admin records the sale. The 10% commission pool is <span className="font-bold">$10,000</span>, split evenly: your downline earns <span className="font-bold">$5,000</span> (5%) — and you earn <span className="font-bold">$5,000</span> (5%) on top, without lifting a finger.
        </div>
      </div>

      {/* Your link */}
      <div className="card-flat p-6 mb-6" data-testid="refer-link-card">
        <div className="label-eyebrow text-[#4B5563] mb-2">Your personal referral link</div>
        <div className="flex items-stretch gap-2 flex-wrap">
          <div className="flex-1 min-w-[260px] font-mono text-sm bg-[#F9FAFB] border border-[#E5E7EB] rounded-md px-3 py-2.5 break-all">
            {data ? referLink : "Loading…"}
          </div>
          <button onClick={() => copy(referLink, "Link copied!")} data-testid="refer-copy-button" className="btn-primary"><Copy size={14} /> Copy</button>
          <button onClick={shareNative} data-testid="refer-share-button" className="btn-secondary"><Share2 size={14} /> Share</button>
        </div>
        {data && (
          <div className="mt-3 text-xs text-[#4B5563]">
            Or share just your code: <code className="font-mono bg-[#F3F4F6] rounded px-2 py-0.5">{data.referral_code}</code>
          </div>
        )}
      </div>

      {/* Earnings */}
      {data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
            <EarningsStat label="Pending payout" value={`$${data.earnings.pending_total.toFixed(2)}`} accent icon={Sparkles} testid="stat-pending" />
            <EarningsStat label="Paid out (lifetime)" value={`$${data.earnings.paid_out_total.toFixed(2)}`} icon={CheckCircle2} testid="stat-paid" />
            <EarningsStat label="Your sales total" value={`$${data.earnings.own_sales_total.toFixed(2)}`} icon={DollarSign} testid="stat-own-sales" />
            <EarningsStat label="Total earnings" value={`$${data.earnings.grand_total.toFixed(2)}`} icon={TrendingUp} testid="stat-grand-total" />
          </div>
          <div className="mb-6 rounded-md bg-[#FFFBEB] border border-[#FCD34D] p-3 text-xs text-[#92400E]">
            <span className="font-bold">Payouts run monthly.</span> Your pending balance ({`$${data.earnings.pending_total.toFixed(2)}`}) will be paid out by admin. Once paid, it moves to "Paid out (lifetime)" and the counter starts fresh for the next cycle.
          </div>
        </>
      )}

      {/* Upline */}
      {data?.upline && (
        <div className="card-flat p-5 mb-6 flex items-center gap-4">
          {data.upline.picture ? (
            <img src={data.upline.picture} alt={data.upline.name} className="h-12 w-12 rounded-full object-cover" />
          ) : (
            <div className="h-12 w-12 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold">{(data.upline.name || "U").charAt(0).toUpperCase()}</div>
          )}
          <div>
            <div className="label-eyebrow text-[#4B5563]">Referred by</div>
            <div className="font-display font-extrabold text-lg text-[#111827]">{data.upline.name}</div>
          </div>
        </div>
      )}

      {/* Downlines */}
      <div className="card-flat p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-eyebrow text-[#4B5563]">Your downlines</div>
            <div className="font-display font-extrabold text-xl flex items-center gap-2"><Users size={18} /> People who signed up under you</div>
          </div>
          {data && <div className="font-display font-black text-3xl text-[#002FA7]">{data.downlines.length}</div>}
        </div>
        {data && data.downlines.length === 0 ? (
          <div className="py-8 text-center text-sm text-[#4B5563]">
            No referrals yet. Share your link above to start building your team.
          </div>
        ) : data ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm" data-testid="downlines-table">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-[#4B5563]">
                  <th className="label-eyebrow font-bold py-3 pr-3">Affiliate</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Leads captured</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Their 5%</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Your 5%</th>
                  <th className="label-eyebrow font-bold py-3 pr-3 hidden md:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody>
                {data.downlines.map((d) => (
                  <tr key={d.user_id} className="border-b border-[#F3F4F6]">
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-3">
                        {d.picture ? (
                          <img src={d.picture} alt={d.name} className="h-8 w-8 rounded-full object-cover" />
                        ) : (
                          <div className="h-8 w-8 rounded-full bg-[#111827] text-white flex items-center justify-center font-bold text-xs">{(d.name || "U").charAt(0).toUpperCase()}</div>
                        )}
                        <div className="min-w-0">
                          <div className="font-bold text-[#111827] truncate">{d.name}</div>
                          <div className="text-xs text-[#4B5563] truncate">{d.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 pr-3 font-display font-black">{d.lead_count}</td>
                    <td className="py-3 pr-3 text-[#111827]">${d.affiliate_commission_total.toFixed(2)}</td>
                    <td className="py-3 pr-3 font-display font-black text-[#002FA7]">${d.your_referral_cut.toFixed(2)}</td>
                    <td className="py-3 pr-3 hidden md:table-cell text-xs text-[#4B5563]">{d.joined_at ? new Date(d.joined_at).toLocaleDateString() : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
      {/* Payouts history */}
      {payouts.length > 0 && (
        <div className="card-flat p-6 mt-6" data-testid="payouts-history">
          <div className="label-eyebrow text-[#4B5563] mb-1">Payout history</div>
          <div className="font-display font-extrabold text-xl mb-4 flex items-center gap-2">
            <CheckCircle2 size={18} className="text-[#166534]" /> Money paid out to you
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-[#E5E7EB] text-[#4B5563]">
                  <th className="label-eyebrow font-bold py-3 pr-3">Date</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Own commission</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Referral bonus</th>
                  <th className="label-eyebrow font-bold py-3 pr-3">Total</th>
                </tr>
              </thead>
              <tbody>
                {payouts.map((p) => (
                  <tr key={p.payout_id} className="border-b border-[#F3F4F6]">
                    <td className="py-3 pr-3 text-[#4B5563]">{new Date(p.paid_at).toLocaleDateString()}</td>
                    <td className="py-3 pr-3">${p.affiliate_amount.toFixed(2)}</td>
                    <td className="py-3 pr-3 text-[#002FA7]">${p.referrer_amount.toFixed(2)}</td>
                    <td className="py-3 pr-3 font-display font-black text-[#166534]">${p.total_amount.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}

function Step({ n, title, body }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] p-4">
      <div className="h-8 w-8 rounded-full bg-[#111827] text-white font-display font-black flex items-center justify-center mb-3">{n}</div>
      <div className="font-display font-extrabold text-[#111827] mb-1">{title}</div>
      <div className="text-sm text-[#4B5563] leading-relaxed">{body}</div>
    </div>
  );
}

function EarningsStat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-[#4B5563]">{label}</div>
        <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent ? "bg-[#002FA7] text-white" : "bg-[#F3F4F6] text-[#111827]"}`}>
          <Icon size={14} />
        </div>
      </div>
      <div className={`mt-3 font-display font-black text-3xl tracking-tight ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value}</div>
    </div>
  );
}
